import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Plus, Upload, X } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { imgSrc } from "../../data/artworks";

const inp = "w-full border border-[#15202B]/15 bg-white px-3 py-2.5 outline-none focus:border-[#15202B] text-[15px]";
const Card = ({ title, hint, children }) => (
  <section className="bg-white p-5 sm:p-6 shadow-sm grid gap-4">
    <div><h2 className="f-display text-2xl">{title}</h2>{hint && <p className="text-sm text-[#55636F] mt-1">{hint}</p>}</div>
    {children}
  </section>
);
const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "cat";

export const SiteSettings = () => {
  const { settings, setSettings } = useSettings();
  const [f, setF] = useState(settings);
  const [busy, setBusy] = useState(false); const [up, setUp] = useState(false);
  const [msg, setMsg] = useState(null);
  const file = useRef(null);
  useEffect(() => { setF(settings); }, [settings]);

  const upload = async (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    setUp(true); setMsg(null);
    try { const fd = new FormData(); fd.append("file", fl); const { data } = await api.post("/admin/upload", fd); setF((p) => ({ ...p, hero_image: data.url })); }
    catch (ex) { setMsg({ err: true, t: apiError(ex, "La photo n'a pas pu être envoyée.") }); } finally { setUp(false); e.target.value = ""; }
  };
  const setWord = (i, v) => setF((p) => ({ ...p, marquee: p.marquee.map((w, j) => (j === i ? v : w)) }));
  const setCat = (i, v) => setF((p) => ({ ...p, categories: p.categories.map((c, j) => (j === i ? { ...c, label: v } : c)) }));
  const addCat = () => setF((p) => {
    const keys = new Set(p.categories.map((c) => c.key));
    let k = `collection-${p.categories.length + 1}`; while (keys.has(k)) k += "x";
    return { ...p, categories: [...p.categories, { key: k, label: "Nouvelle collection", isNew: true }] };
  });
  const save = async () => {
    setBusy(true); setMsg(null);
    const taken = new Set();
    const categories = f.categories.filter((c) => c.label.trim()).map((c) => {
      let key = c.isNew ? slug(c.label) : c.key;
      while (taken.has(key)) key += "-2";
      taken.add(key);
      return { key, label: c.label.trim() };
    });
    try {
      const { data } = await api.put("/admin/settings", { hero_image: f.hero_image, hero_caption: f.hero_caption.trim(), marquee: f.marquee, categories });
      setSettings(data); setMsg({ t: "Enregistré. Les changements sont en ligne." });
    } catch (ex) { setMsg({ err: true, t: apiError(ex) }); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-5" data-testid="admin-site-settings">
      <Card title="Image principale" hint="La grande toile en arche, en haut de la page d'accueil. Une photo en hauteur rend le mieux.">
        <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-4 items-center">
          <img src={imgSrc(f.hero_image)} alt="Image principale actuelle" className="w-24 h-36 object-cover rounded-t-full bg-[#E9E2D6]" data-testid="settings-hero-preview" />
          <div className="grid gap-3">
            <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={upload} data-testid="settings-hero-file" />
            <button type="button" onClick={() => file.current?.click()} disabled={up} className="btn-line justify-self-start !whitespace-normal" data-testid="settings-hero-upload">
              {up ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Changer l'image
            </button>
            <label className="grid gap-1.5 text-xs tracking-[0.12em] uppercase text-[#55636F]">Texte manuscrit sous l'image
              <input className={inp} value={f.hero_caption} maxLength={60} onChange={(e) => setF({ ...f, hero_caption: e.target.value })} placeholder="Vide : aucun texte" data-testid="settings-hero-caption" />
            </label>
          </div>
        </div>
      </Card>

      <Card title="Bandeau défilant" hint="Les mots qui défilent sous le haut de page.">
        <div className="flex flex-wrap gap-2">
          {f.marquee.map((w, i) => (
            <span key={i} className="flex items-center border border-[#15202B]/15 bg-[#F6F1E9]">
              <input value={w} maxLength={40} onChange={(e) => setWord(i, e.target.value)} className="bg-transparent px-3 py-2 outline-none w-40" aria-label={`Mot ${i + 1}`} data-testid={`settings-marquee-word-${i}`} />
              <button type="button" onClick={() => setF({ ...f, marquee: f.marquee.filter((_, j) => j !== i) })} aria-label="Retirer ce mot" className="px-2 text-[#A2372F]"><X className="w-4 h-4" /></button>
            </span>
          ))}
          {f.marquee.length < 20 && (
            <button type="button" onClick={() => setF({ ...f, marquee: [...f.marquee, ""] })} className="px-4 py-2 border border-dashed border-[#8A6420] text-[#8A6420] inline-flex items-center gap-2" data-testid="settings-marquee-add"><Plus className="w-4 h-4" />Ajouter un mot</button>
          )}
        </div>
      </Card>

      <Card title="Collections de la galerie" hint="Les boutons de filtre de la galerie. Une collection qui contient encore des œuvres ne peut pas être retirée.">
        <div className="grid gap-2">
          {f.categories.map((c, i) => (
            <div key={c.key} className="flex gap-2">
              <input className={inp} value={c.label} maxLength={60} onChange={(e) => setCat(i, e.target.value)} aria-label={`Collection ${i + 1}`} data-testid={`settings-category-${i}`} />
              {f.categories.length > 1 && (
                <button type="button" onClick={() => setF({ ...f, categories: f.categories.filter((_, j) => j !== i) })} aria-label="Retirer cette collection" className="px-3 text-[#A2372F]" data-testid={`settings-category-remove-${i}`}><X className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={addCat} className="justify-self-start mt-1 px-4 py-2 border border-dashed border-[#8A6420] text-[#8A6420] inline-flex items-center gap-2" data-testid="settings-category-add"><Plus className="w-4 h-4" />Ajouter une collection</button>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-4">
        <button onClick={save} disabled={busy || up} className="btn-ink" data-testid="settings-save-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Enregistrer</button>
        {msg && <p className={`text-sm ${msg.err ? "text-[#A2372F]" : "text-[#24502C]"}`} data-testid="settings-message">{msg.t}</p>}
      </div>
    </div>
  );
};
